// FolderDropDialog.tsx — CTSK-501. MANDATORY confirmation before any file of a
// directory drop / folder pick is uploaded. Shows the four counts (included,
// ignored, over the per-drop limit, unsupported type), a soft warning for big
// drops, and — when the org setting groupingAutoNameFromFolder allows — the
// "Name a group from this folder" row:
//
//   off     no row
//   prompt  checkbox (ticked by default only when the root folder itself holds
//           files) + an editable name prefilled with the ROOT folder's name
//   always  no checkbox; a caption says a group named after the root folder
//           will be created automatically
//
// Only the root folder's name is ever used (one group per drop, matching the
// one batch id per drop). The name must pass the shared group-label validator;
// the group is applied AFTER the upload by the parent, through the existing
// bulk-edit path. With grouping off for the org the dialog still shows the
// counts (directory drops are walked either way) but no folder / group wording.

import { useEffect, useId, useMemo, useState } from 'react';
import {
  GROUPING_LARGE_DROP_WARNING,
  GROUP_LABEL_MAX_LENGTH,
  normalizeGroupLabel,
  type GroupingAutoNameMode,
} from '@captain-tasko/shared';
import { autoNameDefaults, type FolderDropSummary } from '../lib/dropped-entries.js';

export interface FolderDropChoice {
  /** Group to apply after the upload, or null for none. Already validated. */
  groupName: string | null;
}

function validGroupName(raw: string): string | null {
  const r = normalizeGroupLabel(raw);
  return r.ok ? r.value : null;
}

export function FolderDropDialog({
  summary, groupingEnabled, autoNameMode, maxFiles, onConfirm, onCancel,
}: {
  summary: FolderDropSummary;
  groupingEnabled: boolean;
  autoNameMode: GroupingAutoNameMode;
  maxFiles: number;
  onConfirm: (choice: FolderDropChoice) => void;
  onCancel: () => void;
}) {
  const titleId = useId();
  const defaults = useMemo(() => autoNameDefaults(summary), [summary]);
  const showAutoName = groupingEnabled && autoNameMode !== 'off' && defaults.available;
  const [checked, setChecked] = useState(defaults.checkedByDefault);
  const [name, setName] = useState(defaults.name);
  useEffect(() => {
    setChecked(defaults.checkedByDefault);
    setName(defaults.name);
  }, [defaults]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const { counts } = summary;
  const nameResult = normalizeGroupLabel(name);
  // 'always' uses the root name as is; if it cannot be a group name the drop
  // simply uploads without a group.
  const alwaysName = autoNameMode === 'always' ? validGroupName(defaults.name) : null;
  const promptError = showAutoName && autoNameMode === 'prompt' && checked
    ? (!nameResult.ok ? nameResult.message : nameResult.value === null ? 'Enter a group name, or untick the box.' : null)
    : null;
  const groupName: string | null = !showAutoName
    ? null
    : autoNameMode === 'always'
      ? alwaysName
      : checked && !promptError ? nameResult.ok ? nameResult.value : null : null;
  const canConfirm = counts.included > 0 && !promptError;

  const rowStyle = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '2px 0', fontSize: 13 } as const;
  const rootLabel = summary.rootNames.length > 0 ? summary.rootNames.join(', ') : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="folder-drop-dialog"
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', padding: 'var(--space-6)', maxWidth: 480, width: '90%' }}
      >
        <h3 id={titleId} style={{ margin: '0 0 var(--space-1) 0', fontSize: 16 }}>Upload folder</h3>
        {rootLabel && (
          <div data-testid="folder-drop-root" style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 'var(--space-3)', overflowWrap: 'anywhere' }}>
            {rootLabel}
          </div>
        )}

        <div data-testid="folder-drop-counts" style={{ marginBottom: 'var(--space-3)' }}>
          <div style={rowStyle}><span>Files to upload</span><strong data-testid="folder-count-included">{counts.included}</strong></div>
          <div style={rowStyle}><span>Ignored (e.g. .DS_Store, node_modules)</span><strong data-testid="folder-count-ignored">{counts.ignored}</strong></div>
          <div style={rowStyle}><span>Over the limit of {maxFiles.toLocaleString()} per drop</span><strong data-testid="folder-count-over-cap">{counts.overCap}</strong></div>
          <div style={rowStyle}><span>Unsupported file type</span><strong data-testid="folder-count-unsupported">{counts.unsupported}</strong></div>
          {counts.unreadable > 0 && (
            <div style={rowStyle}><span>Could not be read</span><strong data-testid="folder-count-unreadable">{counts.unreadable}</strong></div>
          )}
        </div>

        {summary.truncated && (
          <p role="note" style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 var(--space-3) 0' }}>
            This folder is very large; counting stopped early, so the over-the-limit number is a minimum.
          </p>
        )}
        {counts.included > GROUPING_LARGE_DROP_WARNING && (
          <p role="alert" data-testid="folder-large-warning" style={{ fontSize: 13, color: 'var(--warning-primary, #B86E4D)', margin: '0 0 var(--space-3) 0' }}>
            Uploading {counts.included.toLocaleString()} files may take several minutes. Keep this tab open until it finishes.
          </p>
        )}
        {counts.included === 0 && (
          <p role="status" style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 var(--space-3) 0' }}>
            Nothing in this folder can be uploaded.
          </p>
        )}

        {showAutoName && autoNameMode === 'prompt' && (
          <div data-testid="folder-autoname-row" style={{ margin: '0 0 var(--space-4) 0' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
              <input
                type="checkbox"
                data-testid="folder-autoname-checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
              />
              Name a group from this folder
            </label>
            <input
              type="text"
              className="input"
              data-testid="folder-autoname-input"
              aria-label="Group name"
              aria-invalid={promptError ? true : undefined}
              maxLength={GROUP_LABEL_MAX_LENGTH * 2}
              value={name}
              disabled={!checked}
              onChange={(e) => setName(e.target.value)}
              style={{ width: '100%', marginTop: 6 }}
            />
            {promptError && <div role="alert" style={{ color: 'var(--danger-primary, #B3261E)', fontSize: 12, marginTop: 4 }}>{promptError}</div>}
          </div>
        )}
        {showAutoName && autoNameMode === 'always' && (
          <p data-testid="folder-autoname-caption" style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 var(--space-4) 0' }}>
            {alwaysName
              ? <>A group named “{alwaysName}” will be created automatically</>
              : <>The folder name can’t be used as a group name, so no group will be created.</>}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="btn btn--primary"
            data-testid="folder-drop-confirm"
            disabled={!canConfirm}
            autoFocus
            onClick={() => onConfirm({ groupName })}
          >
            {`Upload ${counts.included.toLocaleString()} file${counts.included === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
